import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";

import BASE_URL from "../lib/baseUrls";
import { getStoredToken, getStoredUserId } from "../lib/authStorage";

const ChatSocketContext = createContext(null);

const RECONNECT_DELAY_MS = 1500;
const CONNECTION_TIMEOUT_MS = 7000;
const STUCK_RECONNECT_MS = 12000;
const RECONNECT_WATCHDOG_MS = 4000;

const readStoredAuth = () => {
  return {
    token: getStoredToken(),
    userId: getStoredUserId(),
  };
};

const normalizeBody = (body) =>
  typeof body === "string" ? body : JSON.stringify(body ?? {});

/**
 * One authenticated STOMP/SockJS connection for the whole logged-in app.
 *
 * Besides STOMP's normal auto-reconnect, this provider also recovers from
 * browser/network states where the old SockJS connection becomes stale and
 * the normal reconnect timer never gets a clean WebSocket close event.
 */
export const ChatSocketProvider = ({ children }) => {
  const location = useLocation();

  const clientRef = useRef(null);
  const subscriptionsRef = useRef(new Map());
  const subscriptionSequenceRef = useRef(0);
  const pendingPublishesRef = useRef([]);

  const disconnectedSinceRef = useRef(null);
  const restartInProgressRef = useRef(false);

  const [auth, setAuth] = useState(readStoredAuth);
  const [connectionState, setConnectionState] = useState(
    auth.token && auth.userId ? "connecting" : "disconnected"
  );

  const syncAuth = useCallback(() => {
    const next = readStoredAuth();

    setAuth((current) => {
      if (current.token === next.token && current.userId === next.userId) {
        return current;
      }

      return next;
    });
  }, []);

  useEffect(() => {
    syncAuth();

    window.addEventListener("storage", syncAuth);
    window.addEventListener("dealswapify:auth-changed", syncAuth);

    return () => {
      window.removeEventListener("storage", syncAuth);
      window.removeEventListener("dealswapify:auth-changed", syncAuth);
    };
  }, [location.pathname, syncAuth]);

  const installSubscription = useCallback((client, entry) => {
    if (!client?.connected || !entry || entry.subscription) return;

    try {
      entry.subscription = client.subscribe(
        entry.destination,
        (frame) => entry.callback(frame),
        entry.headers
      );
    } catch (error) {
      entry.subscription = null;
      console.error(
        `Failed to subscribe to ${entry.destination}`,
        error
      );
    }
  }, []);

  const installAllSubscriptions = useCallback(
    (client) => {
      subscriptionsRef.current.forEach((entry) => {
        entry.subscription = null;
        installSubscription(client, entry);
      });
    },
    [installSubscription]
  );

  const flushPendingPublishes = useCallback((client) => {
    if (!client?.connected || pendingPublishesRef.current.length === 0) {
      return;
    }

    const queued = pendingPublishesRef.current;
    pendingPublishesRef.current = [];

    queued.forEach((entry) => {
      try {
        client.publish({
          destination: entry.destination,
          body: entry.body,
          headers: entry.headers,
        });
      } catch (error) {
        console.error("Failed to flush queued chat message", error);
        pendingPublishesRef.current.push(entry);
      }
    });
  }, []);

  useEffect(() => {
    const { token, userId } = auth;

    const previousClient = clientRef.current;

    if (previousClient) {
      clientRef.current = null;

      // Do not block creation of the new authenticated client on a stale
      // browser socket. The old client is no longer considered authoritative.
      Promise.resolve(
        previousClient.deactivate?.()
      ).catch((error) => {
        console.warn("Previous chat socket cleanup failed", error);
      });
    }

    subscriptionsRef.current.forEach((entry) => {
      entry.subscription = null;
    });

    disconnectedSinceRef.current = null;
    restartInProgressRef.current = false;

    if (!token || !userId) {
      pendingPublishesRef.current = [];
      setConnectionState("disconnected");
      return undefined;
    }

    let disposed = false;

    const markReconnecting = () => {
      if (disposed || clientRef.current !== client) return;

      if (!disconnectedSinceRef.current) {
        disconnectedSinceRef.current = Date.now();
      }

      setConnectionState(
        navigator.onLine === false
          ? "reconnecting"
          : "reconnecting"
      );
    };

    const client = new Client({
      webSocketFactory: () => new SockJS(`${BASE_URL}/chat`),

      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },

      // STOMP still owns the normal retry loop.
      reconnectDelay: RECONNECT_DELAY_MS,
      connectionTimeout: CONNECTION_TIMEOUT_MS,

      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,

      // Important for browsers/SockJS: when heartbeat/communication fails,
      // discard the stale socket immediately instead of waiting indefinitely
      // for the browser to finish closing it.
      discardWebsocketOnCommFailure: true,

      debug: () => {},
    });

    /**
     * Refresh the token immediately before every initial/reconnect handshake.
     * This avoids a reconnect repeatedly using an older token if auth storage
     * was refreshed while the same provider instance stayed mounted.
     */
    client.beforeConnect = () => {
      const latestToken = getStoredToken();

      if (latestToken) {
        client.connectHeaders = {
          Authorization: `Bearer ${latestToken}`,
        };
      }
    };

    const restartConnection = async (reason) => {
      if (
        disposed ||
        clientRef.current !== client ||
        client.connected ||
        restartInProgressRef.current ||
        navigator.onLine === false
      ) {
        return;
      }

      restartInProgressRef.current = true;
      setConnectionState("reconnecting");

      try {
        console.info(`Restarting shared chat connection: ${reason}`);

        subscriptionsRef.current.forEach((entry) => {
          entry.subscription = null;
        });

        disconnectedSinceRef.current = Date.now();

        /*
         * If STOMP is still active, force-close the current underlying socket.
         * @stomp/stompjs will then use reconnectDelay to create a fresh
         * SockJS instance. If the client somehow became inactive, activate it.
         */
        if (client.active) {
          client.forceDisconnect();
        } else {
          client.activate();
        }
      } catch (error) {
        console.error("Failed to restart shared chat connection", error);

        if (!disposed && clientRef.current === client && !client.active) {
          try {
            client.activate();
          } catch (activateError) {
            console.error(
              "Failed to reactivate shared chat connection",
              activateError
            );
          }
        }
      } finally {
        restartInProgressRef.current = false;
      }
    };

    client.onConnect = () => {
      if (disposed || clientRef.current !== client) return;

      disconnectedSinceRef.current = null;
      restartInProgressRef.current = false;

      setConnectionState("connected");

      installAllSubscriptions(client);
      flushPendingPublishes(client);
    };

    client.onStompError = (frame) => {
      if (disposed || clientRef.current !== client) return;

      console.error(
        "Shared chat STOMP error",
        frame.headers?.message || frame.body
      );

      if (!disconnectedSinceRef.current) {
        disconnectedSinceRef.current = Date.now();
      }

      setConnectionState("error");

      /*
       * Brokers normally close after an ERROR frame. If they do not, forcing
       * the socket closed guarantees STOMP can enter its reconnect path.
       */
      try {
        client.forceDisconnect?.();
      } catch (error) {
        console.warn("Unable to force-close STOMP error socket", error);
      }
    };

    client.onWebSocketError = (error) => {
      if (disposed || clientRef.current !== client) return;

      console.error("Shared chat WebSocket error", error);
      markReconnecting();
    };

    client.onWebSocketClose = () => {
      if (disposed || clientRef.current !== client) return;

      subscriptionsRef.current.forEach((entry) => {
        entry.subscription = null;
      });

      if (client.active) {
        markReconnecting();
      } else {
        setConnectionState("disconnected");
      }
    };

    client.onHeartbeatLost = () => {
      if (disposed || clientRef.current !== client) return;

      console.warn("Shared chat heartbeat lost");
      markReconnecting();
    };

    const handleOnline = () => {
      if (disposed || clientRef.current !== client || client.connected) {
        return;
      }

      // Do not wait for an old retry timer after connectivity comes back.
      void restartConnection("browser online");
    };

    const handleOffline = () => {
      if (disposed || clientRef.current !== client) return;

      if (!disconnectedSinceRef.current) {
        disconnectedSinceRef.current = Date.now();
      }

      setConnectionState("reconnecting");
    };

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        !client.connected &&
        navigator.onLine !== false
      ) {
        void restartConnection("tab became visible");
      }
    };

    const watchdog = window.setInterval(() => {
      if (
        disposed ||
        clientRef.current !== client ||
        client.connected ||
        navigator.onLine === false ||
        restartInProgressRef.current
      ) {
        return;
      }

      const disconnectedSince = disconnectedSinceRef.current;

      if (
        disconnectedSince &&
        Date.now() - disconnectedSince >= STUCK_RECONNECT_MS
      ) {
        void restartConnection("reconnect watchdog");
      } else if (!client.active) {
        /*
         * A client becoming INACTIVE unexpectedly means STOMP itself will not
         * reconnect anymore. Restart it immediately.
         */
        disconnectedSinceRef.current =
          disconnectedSinceRef.current || Date.now();

        try {
          setConnectionState("reconnecting");
          client.activate();
        } catch (error) {
          console.error("Failed to activate inactive chat client", error);
        }
      }
    }, RECONNECT_WATCHDOG_MS);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    setConnectionState("connecting");
    disconnectedSinceRef.current = Date.now();

    clientRef.current = client;
    client.activate();

    return () => {
      disposed = true;

      window.clearInterval(watchdog);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );

      if (clientRef.current === client) {
        clientRef.current = null;
      }

      subscriptionsRef.current.forEach((entry) => {
        entry.subscription = null;
      });

      disconnectedSinceRef.current = null;
      restartInProgressRef.current = false;

      Promise.resolve(
        client.deactivate?.()
      ).catch((error) => {
        console.warn("Chat socket cleanup failed", error);
      });
    };
  }, [auth, flushPendingPublishes, installAllSubscriptions]);

  const subscribe = useCallback(
    (destination, callback, headers = {}) => {
      if (!destination || typeof callback !== "function") {
        return () => {};
      }

      const id = ++subscriptionSequenceRef.current;

      const entry = {
        id,
        destination,
        callback,
        headers,
        subscription: null,
      };

      subscriptionsRef.current.set(id, entry);
      installSubscription(clientRef.current, entry);

      return () => {
        const current = subscriptionsRef.current.get(id);
        if (!current) return;

        try {
          current.subscription?.unsubscribe();
        } catch (error) {
          console.error("Failed to unsubscribe chat topic", error);
        }

        subscriptionsRef.current.delete(id);
      };
    },
    [installSubscription]
  );

  const publish = useCallback(
    (
      destination,
      body,
      {
        headers = {},
        queueIfDisconnected = false,
        queueKey = null,
      } = {}
    ) => {
      if (!destination || !auth.token || !auth.userId) {
        return "dropped";
      }

      const payload = {
        destination,
        body: normalizeBody(body),
        headers,
        queueKey,
      };

      const client = clientRef.current;

      if (client?.connected) {
        try {
          client.publish({
            destination: payload.destination,
            body: payload.body,
            headers: payload.headers,
          });

          return "published";
        } catch (error) {
          console.error("Shared chat publish failed", error);

          /*
           * A socket that claims to be connected but cannot publish should be
           * considered unhealthy. Force its close so auto-reconnect starts.
           */
          try {
            client.forceDisconnect?.();
          } catch {
            // The queued message below is still preserved.
          }

          if (!queueIfDisconnected) {
            return "dropped";
          }
        }
      } else if (!queueIfDisconnected) {
        return "dropped";
      }

      if (queueKey) {
        pendingPublishesRef.current =
          pendingPublishesRef.current.filter(
            (entry) => entry.queueKey !== queueKey
          );
      }

      // Protect the browser from an unbounded queue during a long outage.
      if (pendingPublishesRef.current.length >= 100) {
        pendingPublishesRef.current.shift();
      }

      pendingPublishesRef.current.push(payload);

      return "queued";
    },
    [auth.token, auth.userId]
  );

  const value = useMemo(
    () => ({
      token: auth.token,
      userId: auth.userId,
      isAuthenticated: Boolean(auth.token && auth.userId),
      connectionState,
      connected: connectionState === "connected",
      subscribe,
      publish,
    }),
    [
      auth.token,
      auth.userId,
      connectionState,
      publish,
      subscribe,
    ]
  );

  return (
    <ChatSocketContext.Provider value={value}>
      {children}
    </ChatSocketContext.Provider>
  );
};

export const useChatSocket = () => {
  const context = useContext(ChatSocketContext);

  if (!context) {
    throw new Error(
      "useChatSocket must be used inside ChatSocketProvider"
    );
  }

  return context;
};
